import React, { useState, useEffect } from 'react';
import { getPendingRecharges, approveRejectRecharge, getAllRecharges, getAdminRechargePaymentProof } from '../../services/api';
import { formatDateTimeDDMMYYYY } from '../../utils/dateUtils';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { showSuccessToast, showErrorToast } from '../../utils/toast';
import { Eye } from 'lucide-react';

const RechargeRequests = () => {
  const [allRequests, setAllRequests] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [processing, setProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');
  const [pagination, setPagination] = useState({ page: 0, totalPages: 0, totalElements: 0 });
  const pageSize = 20;
  const [fetchingProof, setFetchingProof] = useState(false);

  const handleViewProof = async (requestId) => {
    setFetchingProof(true);
    try {
      const response = await getAdminRechargePaymentProof(requestId);
      const contentType = response.headers['content-type'] || 'image/png';
      const file = new Blob([response.data], { type: contentType });
      const fileURL = URL.createObjectURL(file);
      
      if (contentType.includes('image') || contentType.includes('pdf')) {
        window.open(fileURL, '_blank');
      } else {
        const link = document.createElement('a');
        link.href = fileURL;
        link.download = `payment-proof-${requestId}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      console.error('Failed to load payment proof:', err);
      showErrorToast('Failed to load payment proof file');
    } finally {
      setFetchingProof(false);
    }
  };

  useEffect(() => { loadAllRequests(activeTab); }, [activeTab]);

  const loadAllRequests = async (tab = activeTab) => {
    setLoading(true);
    try {
      // Fetch up to 1000 records to support the client-side pagination
      const res = tab === 'pending' ? await getPendingRecharges(0, 1000) : await getAllRecharges(0, 1000);
      let data = res.data;
      if (Array.isArray(data)) {
        // If we are in the history tab, filter out PENDING requests
        if (tab === 'history') {
          data = data.filter(r => r.status !== 'PENDING');
        }
        
        // Store the result
        setAllRequests(data);
        
        // Calculate pagination
        const totalPages = Math.ceil(data.length / pageSize);
        const startIndex = 0;
        const endIndex = startIndex + pageSize;
        const paginatedData = data.slice(startIndex, endIndex);
        
        // Set current page data
        setRequests(paginatedData);
        setPagination({
          page: 0,
          totalPages: totalPages,
          totalElements: data.length
        });
      } else {
        // Handle unexpected format
        setAllRequests([]);
        setRequests([]);
        setPagination({ page: 0, totalPages: 0, totalElements: 0 });
      }
    } catch (err) {
      console.error('Failed to load recharge requests:', err);
      
      // Enhanced error handling
      let errorMessage = 'Failed to load recharge requests';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      showErrorToast(errorMessage);
      setAllRequests([]);
      setRequests([]);
      setPagination({ page: 0, totalPages: 0, totalElements: 0 });
    } finally {
      setLoading(false);
    }
  };

  const loadRequests = (page = 0) => {
    // Client-side pagination
    const startIndex = page * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedData = allRequests.slice(startIndex, endIndex);
    
    setRequests(paginatedData);
    setPagination({
      page: page,
      totalPages: Math.ceil(allRequests.length / pageSize),
      totalElements: allRequests.length
    });
  };

  const handleApproval = async (approved) => {
    setProcessing(true);
    try {
      const response = await approveRejectRecharge(selected.requestId, { approved, remarks });
      
      // Handle API response properly
      if (response.data) {
        const { status, message } = response.data;
        
        if (status === 'Success') {
          showSuccessToast(message || `${approved ? 'Recharge approved' : 'Recharge rejected'} successfully`);
        } else {
          showErrorToast(message || `Failed to ${approved ? 'approve' : 'reject'} recharge`);
        }
      } else {
        // Fallback for older response format
        showSuccessToast(`${approved ? 'Recharge approved' : 'Recharge rejected'} successfully`);
      }
      
      setSelected(null);
      setRemarks('');
      loadAllRequests(); // Reload all data after approval
    } catch (err) {
      console.error('Approval error:', err);
      
      // Enhanced error handling
      let errorMessage = `Failed to ${approved ? 'approve' : 'reject'} recharge request`;
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      showErrorToast(errorMessage);
    } finally {
      setProcessing(false);
    }
  };

  if (loading && requests.length === 0) return <div className="flex justify-center p-8">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Recharge Requests</h1>
        <div className="text-sm text-gray-600">
          {pagination.totalElements} total requests
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full mb-6">
        <TabsList className="grid w-full grid-cols-2 max-w-[400px]">
          <TabsTrigger value="pending">Pending Requests</TabsTrigger>
          <TabsTrigger value="history">All Requests (History)</TabsTrigger>
        </TabsList>
      </Tabs>

      {requests.length === 0 && !loading ? (
        <Card>
          <CardContent className="py-8 text-center text-gray-500">
            No {activeTab === 'pending' ? 'pending' : ''} requests
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle>{activeTab === 'pending' ? 'Pending' : 'All'} Recharge Requests ({pagination.totalElements})</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table className="min-w-[800px]">
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>Request ID</TableHead>
                      <TableHead>Merchant</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Bank</TableHead>
                      <TableHead>Reference</TableHead>
                      <TableHead className="text-center">Proof</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right pr-6">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {requests.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell className="font-mono text-sm">{r.requestId}</TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">{r.merchant?.merchantName || 'Unknown'}</div>
                            <div className="text-xs text-gray-500">{r.merchant?.merchantId || 'N/A'}</div>
                          </div>
                        </TableCell>
                        <TableCell className="font-semibold text-green-600">
                          ₹{r.amount?.toLocaleString() || '0'}
                        </TableCell>
                        <TableCell>{r.bankName || 'N/A'}</TableCell>
                        <TableCell className="font-mono text-xs">
                          {r.transactionReference || 'N/A'}
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleViewProof(r.requestId)}
                            className="h-8 text-blue-600 hover:text-blue-800 hover:bg-blue-50 inline-flex items-center gap-1 px-2"
                            title="View Payment Proof"
                          >
                            <Eye className="h-4 w-4" />
                            <span className="text-xs font-semibold">View</span>
                          </Button>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge className={
                            r.status === 'APPROVED' ? 'bg-green-100 text-green-800' :
                            r.status === 'REJECTED' ? 'bg-red-100 text-red-800' :
                            'bg-amber-100 text-amber-800'
                          }>
                            {r.status || 'PENDING'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">
                          {formatDateTimeDDMMYYYY(r.createdAt)}
                        </TableCell>
                        <TableCell className="text-right pr-6">
                          {r.status === 'PENDING' ? (
                            <Button size="sm" onClick={() => setSelected(r)}>Review</Button>
                          ) : (
                            <Button size="sm" variant="outline" onClick={() => setSelected(r)}>View Details</Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {requests.length === 0 && !loading && (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-8 text-gray-500">
                          No {activeTab === 'pending' ? 'pending' : ''} requests
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Pagination Controls */}
          {pagination.totalPages > 0 && (
            <div className="flex justify-between items-center mt-4 px-4 pb-4">
              <div className="text-sm text-gray-600">
                {pagination.page * pageSize + 1}-{Math.min((pagination.page + 1) * pageSize, pagination.totalElements)} of {pagination.totalElements}
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => loadRequests(pagination.page - 1)}
                  disabled={pagination.page === 0 || loading}
                >
                  ←
                </Button>
                {Array.from({ length: pagination.totalPages }, (_, i) => (
                  <Button
                    key={i}
                    variant={i === pagination.page ? 'default' : 'outline'}
                    size="sm"
                    className="w-8 h-8 p-0"
                    onClick={() => loadRequests(i)}
                  >
                    {i + 1}
                  </Button>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => loadRequests(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages - 1 || loading}
                >
                  →
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Review Recharge - {selected?.requestId}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {/* Recharge Details */}
            <div className="p-3 bg-gray-50 rounded">
              <h4 className="font-semibold text-gray-700 mb-2">Recharge Details</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <p><strong>Amount:</strong> ₹{selected?.amount.toLocaleString()}</p>
                <p><strong>Bank:</strong> {selected?.bankName}</p>
                <p><strong>Reference:</strong> {selected?.transactionReference || 'N/A'}</p>
                <p><strong>Status:</strong> 
                  <Badge className={
                    selected?.status === 'APPROVED' ? 'bg-green-100 text-green-800' :
                    selected?.status === 'REJECTED' ? 'bg-red-100 text-red-800' :
                    'bg-amber-100 text-amber-800'
                  }>
                    {selected?.status}
                  </Badge>
                </p>
              </div>
              {selected?.remarks && (
                <p className="mt-2"><strong>Remarks:</strong> {selected?.remarks}</p>
              )}
              <div className="mt-3 pt-3 border-t border-gray-200 flex justify-end">
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => handleViewProof(selected.requestId)}
                  disabled={fetchingProof}
                >
                  {fetchingProof ? 'Fetching...' : 'View/Download Payment Proof'}
                </Button>
              </div>
            </div>

            {/* Merchant Details */}
            {selected?.merchant && (
              <div className="p-3 bg-blue-50 rounded">
                <h4 className="font-semibold text-gray-700 mb-2">Merchant Details</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <p><strong>Name:</strong> {selected.merchant.merchantName}</p>
                  <p><strong>ID:</strong> {selected.merchant.merchantId}</p>
                  <p><strong>Company:</strong> {selected.merchant.companyName}</p>
                  <p><strong>Email:</strong> {selected.merchant.email}</p>
                  <p><strong>Phone:</strong> {selected.merchant.phone}</p>
                  <p><strong>Wallet Balance:</strong> ₹{selected.merchant.walletBalance?.toFixed(2) || '0.00'}</p>
                  <p><strong>Status:</strong> {selected.merchant.status}</p>
                  <p><strong>KYC Status:</strong> 
                    <Badge className={
                      selected.merchant.kycStatus === 'APPROVED' ? 'bg-green-100 text-green-800' :
                      selected.merchant.kycStatus === 'PENDING' ? 'bg-amber-100 text-amber-800' :
                      'bg-red-100 text-red-800'
                    }>
                      {selected.merchant.kycStatus}
                    </Badge>
                  </p>
                </div>
              </div>
            )}

            {/* Action Section - Only show for pending requests */}
            {selected?.status === 'PENDING' && (
              <>
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">Remarks <span className="text-gray-400 font-normal">(optional)</span></p>
                  <Textarea 
                    placeholder="Add remarks for this decision..." 
                    value={remarks} 
                    onChange={(e) => setRemarks(e.target.value)} 
                  />
                </div>
                
                <div className="flex gap-2">
                  <Button className="flex-1" onClick={() => handleApproval(true)} disabled={processing}>Approve</Button>
                  <Button variant="destructive" className="flex-1" onClick={() => handleApproval(false)} disabled={processing}>Reject</Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RechargeRequests;
